import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Supercluster from "supercluster";

import {
  INITIAL_REGION,
  MAP_FILTER_CATEGORIES,
  type MapRegion,
} from "../constants";
import type { MapPoi, MapPoiCategory } from "../services/map.types";

type PoiFeatureProperties = {
  poi: MapPoi;
};

type ClusterFeature = Supercluster.ClusterFeature<PoiFeatureProperties>;
type PointFeature = Supercluster.PointFeature<PoiFeatureProperties>;

export type ClusterMode = "category" | "poi";

export type MapClusterItem =
  | {
      type: "cluster";
      clusterId: number;
      latitude: number;
      longitude: number;
      pointCount: number;
      category?: MapPoiCategory;
    }
  | {
      type: "poi";
      poi: MapPoi;
    };

export const CATEGORY_CLUSTER_ICON_ZOOM = 9;

const POI_CLUSTER_MAX_ZOOM = 14;
const CATEGORY_CLUSTER_MAX_ZOOM = 13;
const MAX_RENDERED_MARKERS = 100;

const getZoomLevel = (longitudeDelta: number) =>
  Math.max(0, Math.min(20, Math.round(Math.log2(360 / longitudeDelta))));

const toBBox = (region: MapRegion): [number, number, number, number] => [
  region.longitude - region.longitudeDelta / 2,
  region.latitude - region.latitudeDelta / 2,
  region.longitude + region.longitudeDelta / 2,
  region.latitude + region.latitudeDelta / 2,
];

const toClusterItem = (
  feature: ClusterFeature | PointFeature,
  category?: MapPoiCategory
): MapClusterItem => {
  const [longitude, latitude] = feature.geometry.coordinates;
  const { properties } = feature;

  if ("cluster" in properties && properties.cluster) {
    return {
      type: "cluster",
      clusterId: properties.cluster_id,
      latitude,
      longitude,
      pointCount: properties.point_count,
      category,
    };
  }

  return {
    type: "poi",
    poi: properties.poi,
  };
};

const toFeatures = (pois: MapPoi[]) =>
  pois.map((poi) => ({
    type: "Feature" as const,
    properties: { poi },
    geometry: {
      type: "Point" as const,
      coordinates: [poi.longitude, poi.latitude] as [number, number],
    },
  }));

function buildPoiClusterIndex(pois: MapPoi[]) {
  const index = new Supercluster<PoiFeatureProperties>({
    radius: 56,
    maxZoom: POI_CLUSTER_MAX_ZOOM,
    minPoints: 2,
  });
  index.load(toFeatures(pois));
  return index;
}

function buildCategoryClusterIndexes(
  poisByCategory: Map<MapPoiCategory, MapPoi[]>
) {
  const indexes = new Map<MapPoiCategory, Supercluster<PoiFeatureProperties>>();

  MAP_FILTER_CATEGORIES.forEach((category) => {
    const categoryPois = poisByCategory.get(category) ?? [];
    const index = new Supercluster<PoiFeatureProperties>({
      radius: 72,
      maxZoom: CATEGORY_CLUSTER_MAX_ZOOM,
      minPoints: 2,
    });
    index.load(toFeatures(categoryPois));
    indexes.set(category, index);
  });

  return indexes;
}

function limitRenderedMarkers(items: MapClusterItem[]): MapClusterItem[] {
  if (items.length <= MAX_RENDERED_MARKERS) {
    return items;
  }

  const clusters = items.filter((item) => item.type === "cluster");
  if (clusters.length >= MAX_RENDERED_MARKERS) {
    return clusters.slice(0, MAX_RENDERED_MARKERS);
  }

  const remaining = MAX_RENDERED_MARKERS - clusters.length;
  const points = items
    .filter((item) => item.type === "poi")
    .slice(0, remaining);

  return [...clusters, ...points];
}

export function useMapClusters(pois: MapPoi[], mode: ClusterMode = "poi") {
  const poiClusterIndexRef = useRef<Supercluster<PoiFeatureProperties> | null>(
    null
  );
  const categoryClusterIndexesRef = useRef<
    Map<MapPoiCategory, Supercluster<PoiFeatureProperties>>
  >(new Map());

  const [region, setRegion] = useState<MapRegion>(INITIAL_REGION);
  const [indexVersion, setIndexVersion] = useState(0);

  const poisByCategory = useMemo(() => {
    const grouped = new Map<MapPoiCategory, MapPoi[]>(
      MAP_FILTER_CATEGORIES.map((category) => [category, []])
    );

    pois.forEach((poi) => {
      grouped.get(poi.category)?.push(poi);
    });

    return grouped;
  }, [pois]);

  const poisSignature = useMemo(
    () =>
      `${mode}:${pois.length}:${pois[0]?.id ?? ""}:${
        pois[pois.length - 1]?.id ?? ""
      }`,
    [mode, pois]
  );

  useEffect(() => {
    if (mode === "category") {
      categoryClusterIndexesRef.current =
        buildCategoryClusterIndexes(poisByCategory);
      poiClusterIndexRef.current = null;
      setIndexVersion((version) => version + 1);
      return;
    }

    poiClusterIndexRef.current = buildPoiClusterIndex(pois);
    categoryClusterIndexesRef.current = new Map();
    setIndexVersion((version) => version + 1);
  }, [poisSignature, poisByCategory, pois, mode]);

  const clusters = useMemo(() => {
    if (pois.length === 0) {
      return [] as MapClusterItem[];
    }

    const bbox = toBBox(region);
    const zoom = getZoomLevel(region.longitudeDelta);
    let items: MapClusterItem[];

    if (mode === "category") {
      items = MAP_FILTER_CATEGORIES.flatMap((category) => {
        const index = categoryClusterIndexesRef.current.get(category);
        if (!index) return [];

        const features = index.getClusters(bbox, zoom) as (
          | ClusterFeature
          | PointFeature
        )[];
        return features.map((feature) => toClusterItem(feature, category));
      });
    } else {
      const index = poiClusterIndexRef.current;
      if (!index) {
        return [] as MapClusterItem[];
      }

      items = (
        index.getClusters(bbox, zoom) as (ClusterFeature | PointFeature)[]
      ).map((feature) => toClusterItem(feature));
    }

    return limitRenderedMarkers(items);
    // indexVersion forces cluster index rebuild after pois change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pois.length, region, mode, indexVersion]);

  const resetRegion = useCallback(() => {
    setRegion(INITIAL_REGION);
  }, []);

  const handleRegionChangeComplete = useCallback((nextRegion: MapRegion) => {
    setRegion(nextRegion);
  }, []);

  const getClusterExpansionRegion = useCallback(
    (
      clusterId: number,
      latitude: number,
      longitude: number,
      category?: MapPoiCategory
    ): MapRegion => {
      const index =
        mode === "category" && category
          ? categoryClusterIndexesRef.current.get(category)
          : poiClusterIndexRef.current;

      if (!index) {
        return { ...INITIAL_REGION, latitude, longitude };
      }

      const expansionZoom = Math.min(
        index.getClusterExpansionZoom(clusterId),
        mode === "category" ? CATEGORY_CLUSTER_MAX_ZOOM : POI_CLUSTER_MAX_ZOOM
      );
      const longitudeDelta = 360 / 2 ** expansionZoom;
      const latitudeDelta = longitudeDelta;

      return {
        latitude,
        longitude,
        latitudeDelta,
        longitudeDelta,
      };
    },
    [mode]
  );

  const zoom = getZoomLevel(region.longitudeDelta);
  const showCategoryClusterIcons =
    mode === "category" && zoom >= CATEGORY_CLUSTER_ICON_ZOOM;

  return {
    clusters,
    region,
    zoom,
    showCategoryClusterIcons,
    resetRegion,
    handleRegionChangeComplete,
    getClusterExpansionRegion,
  };
}
